import { useI18nContext } from "@i18n/i18n-react";
import { preloadQuery } from "@relay";
import { TabNavLink } from "@shadcn/pro-blocks/application/page-headers/page-header-3";
import { Badge } from "@shadcn/ui/badge";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { formatToCurrency } from "@utils/currency";
import { uniqBy } from "es-toolkit";
import { PropsWithChildren, useState, useEffect } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import z from "zod";
import { HubScannerDrawer } from "~/components/HubScannerDrawer";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { ProductDrawer } from "~/components/ProductDrawer";
import { ProductTrack } from "~/components/ProductTrack";
import { useCart } from "~/contexts/CartContext";
import { useHubSession } from "@contexts/HubSessionContext";
import { instantBuyCatalogueQuery } from "./__generated__/instantBuyCatalogueQuery.graphql";
import { HubConnectionDrawer } from "~/components/HubConnectionDrawer";
import { applyTheme, getCurrentTheme } from "@utils/themes";
import { getCategoryName } from "@utils/language";
import { SearchBar } from "~/components/SearchBar";
import { PercentIcon } from "lucide-react";
import { isDiscountName } from "@utils/discount";


const CartButton = ({ onClickWithoutHub }: { onClickWithoutHub: () => void }) => {
	const useCartStore = useCart();
	const totalPrice = useCartStore((state) => state.totalPrice());
	const totalItems = useCartStore((state) => state.totalItems());
	const { hub: scannedHub } = useHubSession();
	const { hubMeta } = usePreloadedQuery<instantBuyCatalogueQuery>(query, Route.useLoaderData());
	const hub = hubMeta.hub;
	const i18n = useI18nContext().LL["new-ui"];

	if (totalItems === 0) {
		return null;
	}

	const isConnectedToHub = scannedHub && scannedHub.id === hub.id;

	if (isConnectedToHub) {
		return (
			<Button className="w-full font-breit font-normal shadow-xl" size="lg" asChild>
				<Link to="/$hubMetaName/instant-buy/order-summary" from={Route.fullPath}>
					{i18n.VIEW_ORDER()} <Badge variant="secondary" className="font-breit text-xs font-semibold leading-4">{formatToCurrency(totalPrice)}</Badge>
				</Link>
			</Button>
		);
	}

	return (
		<Button className="w-full font-breit font-normal shadow-xl" size="lg" onClick={onClickWithoutHub}>
			{i18n.VIEW_ORDER()} <Badge variant="secondary" className="font-breit text-xs font-semibold leading-4">{formatToCurrency(totalPrice)}</Badge>
		</Button>
	);
};

const InstantBuyTabNavLinks = ({ children }: PropsWithChildren) => {
	return (
		<nav className="border-border border-b">
			<div className="container mx-auto flex overflow-x-auto px-6 font-breit snap-x snap-mandatory scroll-px-6">
				{children}
			</div>
		</nav>
	);
};

export const query = graphql`
	query instantBuyCatalogueQuery($hubName: String!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			street
			organization {
				id
				slug
			}
			hub @required(action: THROW) {
				id
				slug
				meta @required(action: THROW) {
					id
					name
					services
					lat
					lng
				}

				products(filter: { active: true }) {
					...ProductTrackFragment
					nodes {
						id
						product {
							id
							name
							brand {
								id
								organization {
									id
									name
								}
							}
							category {
									id
									name
									i18N
							}
						}
						...ProductDrawerFragment
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/instant-buy/")({
	beforeLoad: () => {
		document.title = 'bacehub | Instant Buy'
	},
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			// Tab can be either recommended or a brand, for brands it will be the orgId of the brand in there which is not statically knowable
			tab: z.union([z.literal("recommended"), z.string()]).default("recommended"),
			type: z.enum(["brand", "category", "none"]).optional(),
			hubProductId: z.string().optional(),
		})
	),
	loader: async (ctx) => {
		return preloadQuery<instantBuyCatalogueQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
		});
	},
});

export default function RouteComponent() {
	const { hubProductId, tab, type } = Route.useSearch();
	const [hasDiscount, setHasDiscount] = useState(false);
	const { hubMeta } = usePreloadedQuery<instantBuyCatalogueQuery>(query, Route.useLoaderData());
	const hub = hubMeta.hub;
	const { hub: scannedHub } = useHubSession();
	const [isScannerOpen, setIsScannerOpen] = useState(false);
	const [connectDrawerOpen, setConnectDrawerOpen] = useState(false);
	const [waitingForConnection, setWaitingForConnection] = useState(false);
	const navigate = useNavigate();
	const useCartStore = useCart();
	const { locale, LL } = useI18nContext();
	const i18n = LL["new-ui"];
	const [searchValue, setSearchValue] = useState("");



	// Set theme based on hub's organization
	const organizationSlug = hubMeta.organization.slug;
	// TODO: change this
	const targetTheme = organizationSlug === "thalia" || organizationSlug?.startsWith("thalia-") ? "thalia" : "default";

	if (getCurrentTheme() !== targetTheme) {
		applyTheme(targetTheme);
	}

	const categories = uniqBy(
		hub.products.nodes
			.filter(p => !isDiscountName(p.product.name))
			.filter((p) => p.product.category)
			.map((p) => p.product.category),
		(b) => b?.id
	);

	const selectedProduct = hub.products.nodes.find((p) => p.id === hubProductId);

	// Reset cart and waiting state when navigating to a different hub
	useEffect(() => {
		const cartItems = useCartStore.getState().items;
		const hasItems = cartItems.length > 0;

		// If cart has items, check if they belong to the current hub
		if (hasItems) {
			// Check if any cart item doesn't belong to current hub products
			const currentHubProductIds = hub.products.nodes.map((p) => p.id);
			const cartBelongsToCurrentHub = cartItems.every((item) => currentHubProductIds.includes(item.id));

			// Clear cart if items are from a different hub
			if (!cartBelongsToCurrentHub) {
				useCartStore.getState().clearCart();
			}
		}

		// Reset waiting state when switching to a different hub
		setWaitingForConnection(false);
	}, [hubMeta.name, hub.products.nodes]);

	// Navigate to order-summary when hub is connected after clicking View Order
	useEffect(() => {
		if (waitingForConnection && scannedHub && scannedHub.id === hub.id) {
			setWaitingForConnection(false);
			setConnectDrawerOpen(false);
			navigate({
				to: "/$hubMetaName/instant-buy/order-summary",
				params: { hubMetaName: hubMeta.name },
			});
		}
	}, [scannedHub, hub.id, waitingForConnection, navigate, hubMeta.name]);

	const handleViewOrderWithoutHub = () => {
		setWaitingForConnection(true);
		setConnectDrawerOpen(true);
	};

	const hasItemsInCart = useCartStore((state) => state.totalItems()) > 0;

	const footer = () => {
		if (hasItemsInCart) {
			return <CartButton onClickWithoutHub={handleViewOrderWithoutHub} />;
		}

		return null;
	};

	return (
		<>
			<PageContainer footer={footer()}>
				<AppHeader location={{ address: hubMeta.street }} service="INSTANT_BUY" />
				<SearchBar value={searchValue} onChange={setSearchValue} />
				<InstantBuyTabNavLinks>
					<TabNavLink
						label={i18n.ALL_PRODUCTS()}
						from="/$hubMetaName/instant-buy/"
						search={{ tab: "recommended" }}
					/>
					{hasDiscount ?
						<TabNavLink
							icon={PercentIcon}
							label={i18n.DISCOUNTED()}
							from="/$hubMetaName/instant-buy/"
							search={{ tab: "discounted" }} />
						: null}
					{categories.filter(category => !!category).map((category, index) => (
						<TabNavLink key={index} label={getCategoryName(category, locale)} from="/$hubMetaName/instant-buy/" search={{ tab: category.name, type: "category" }} />
					))}
				</InstantBuyTabNavLinks>
				<ProductTrack
					searchValue={searchValue}
					tab={tab}
					type={type}
					products={hub.products}
					categories={categories}
					setHasDiscount={setHasDiscount}
				/>
				{selectedProduct && <ProductDrawer
					hubId={hub.id}
					product={selectedProduct}
					products={hub.products}
					onClose={() =>
						navigate({
							from: Route.fullPath,
							to: ".",
							search: (prev) => ({
								...prev,
								hubProductId: undefined,
							}),
							replace: true,
						})
					}
				/>}
				<HubScannerDrawer isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} expectedHubId={hub.id} />
			</PageContainer>

			<HubConnectionDrawer
				isOpen={connectDrawerOpen}
				onOpenChange={(open) => {
					setConnectDrawerOpen(open);
					// Reset waiting state if drawer is closed without connecting
					if (!open) {
						setWaitingForConnection(false);
					}
				}}
				hubData={{
					id: hub.id,
					name: hub.meta.name,
					lat: hub.meta.lat,
					lng: hub.meta.lng,
				}}
			/>
		</>
	);
}
